import fileDownload from 'js-file-download';
import type {MRT_ColumnDef} from 'mantine-react-table';
import React, {useMemo, useState} from 'react';
import type {VendorSimpleResponse} from 'src/api/ProactApiClient';
import {ModuleEnum, PermissionOperationEnum} from 'src/api/ProactApiClient';
import {Pill} from 'src/design/atoms/Pill';
import {ActiveInactivePills} from 'src/shared/constants/PillConstants';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {ActiveStateDataOptions} from 'src/shared/constants/columnFilterData';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {PermissionGuard} from 'src/app/security/authorization/PermissionGuard';
import {SidebarLinkVendors} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {
  useExportVendorsMutation,
  useGetVendorListV2Query,
} from 'src/redux/queries/vendor';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {VendorImportModal} from './VendorImportModal';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';
export const VendorList: React.FC = () => {
  const {setAlert} = useAlert();
  const navigate = useAppNavigate();
  const [exportVendors, {isLoading: isExporting}] = useExportVendorsMutation();
  const [showImport, setShowImport] = useState<boolean>(false);

  const columns = useMemo<MRT_ColumnDef<VendorSimpleResponse>[]>(
    () => [
      {
        accessorKey: 'active',
        header: 'Active',
        size: 110,
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: ActiveStateDataOptions,
        },
        Cell: ({cell}) => (
          <Pill {...ActiveInactivePills(cell.getValue<boolean>())} />
        ),
      },
      {
        accessorKey: 'name',
        header: 'Name',
      },
      {
        id: 'certificateOfInsuranceExpiresAt',
        accessorFn: row =>
          row.certificateOfInsuranceExpiresAt
            ? new Date(row.certificateOfInsuranceExpiresAt)
            : null,
        filterVariant: 'date',
        header: 'COI Expires At',
        Cell: ({cell}) => (
          <>{cell?.getValue<Date>()?.toString().toShortDate()}</>
        ),
      },
      {
        accessorKey: 'email',
        header: 'Email',
      },
      {
        accessorKey: 'phoneNumber',
        header: 'Phone Number',
      },
      {
        accessorKey: 'manager.name',
        header: 'Manager',
      },
      {
        accessorKey: 'manager.email',
        header: 'Manager Email',
      },
    ],
    []
  );

  const tableState = useTableState({
    identifier: 'vendor',
    columns: columns,
    defaultColumnFilters: [
      {
        id: 'active',
        value: ['true'],
      },
    ],
    defaultColumnVisibility: {
      jobCode: false,
    },
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: [{id: 'name', desc: false}],
    defaultShowColumnFilters: true,
  });
  const {start, length, filters, filterFns, sorting} = tableState;

  const {data, isLoading, isError, isFetching, refetch} =
    useGetVendorListV2Query({
      start: start,
      length: length,
      filters: filters,
      filterFns: filterFns,
      sorting: sorting,
    });
  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      columns: columns,
      data: rows,
      getRowId: row => String(row.id),
      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.View
            onClick={() =>
              navigate(PathUtility.VendorsView(row.original.id, 'overview'))
            }
          />
        </TableRowActions>
      ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Actions>
            <PermissionGuard
              module={ModuleEnum.Vendors}
              operation={PermissionOperationEnum.Create}>
              <TableToolbar.Action variant="create" onClick={handleAddClick}>
                New Vendor
              </TableToolbar.Action>
            </PermissionGuard>
            <PermissionGuard
              module={ModuleEnum.Vendors}
              operation={PermissionOperationEnum.Export}>
              <TableToolbar.Action
                variant="export"
                onClick={handleExport}
                loading={isExporting}
              />
            </PermissionGuard>
            <PermissionGuard
              module={ModuleEnum.Vendors}
              operation={PermissionOperationEnum.Import}>
              <TableToolbar.Action
                variant="import"
                onClick={() => setShowImport(true)}
              />
            </PermissionGuard>
          </TableToolbar.Actions>
        </TableToolbar>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  const handleImportSuccess = () => {
    setShowImport(false);

    setAlert(
      'Your file has been successfully uploaded. You will receive an email after it has been imported with more information.',
      'success'
    );
  };

  const handleExport = async () => {
    try {
      const response = await exportVendors().unwrap();
      fileDownload(
        response!.data,
        `vendor_export_${new Date().toString().toShortDate()}.csv`
      );
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  function handleAddClick(): void {
    navigate(PathUtility.VendorsCreate());
  }

  return (
    <>
      <PageHeader>
        <PageHeader.Title
          icon={SidebarLinkVendors.icon}
          subtitle="Manage vendor companies">
          Vendors
        </PageHeader.Title>
      </PageHeader>
      {table}

      <VendorImportModal
        show={showImport}
        onClose={() => setShowImport(false)}
        onImportSuccess={handleImportSuccess}
      />
    </>
  );
};
